'use client'
import {
    toInteger,
    includes,
    filter,
    compact,
    toLower,
    isArray,
} from 'lodash'
import { makeAppointmentStatusCountToday, ListSortDirection, findTableauManagersInManagers, findProviderIdsInManagers } from '@docpace/shared-ts-utils'
import { AdminProviderDetailFragment, ManagerOverviewFragment } from '@docpace/shared-graphql/fragments'
import { ProviderDetailFragment, ProviderOverviewFragment } from '@docpace/shared-graphql/fragments'
import { ProviderDepartmentOptionSort } from './useSortProviderDepartmentOptions'
import { ManagerDetailFragment } from '@docpace/shared-graphql/fragments'
import { AppointmentCountItem, useAppointmentCounts } from './useAppointmentCounts'
import { useSortListItems } from './useSortListItems'

type ExtendedProviderBase = ProviderDetailFragment &
    Partial<Pick<AdminProviderDetailFragment, 'managerProviderPermissions'>>

export interface ExtendedProvider extends ExtendedProviderBase {
    // These fields are consumed by manager provider UIs but are not present in all graphql fragment variants.
    middleInitial?: string | null
    specialty?: string | null
    isFavorited: boolean
    isSelected: boolean
    appointmentCountNode: AppointmentCountItem
    appointmentCountToday: number
    hasTableauUsers?: boolean
    hasManagerDashboardUsers?: boolean
}

const EMPTY_APPOINTMENT_COUNT_ITEM: AppointmentCountItem = {
    managerAppointmentCount: 0,
    appointmentCount: 0,
    nodes: [],
    departmentAppointmentCounts: new Map(),
    departmentManagerAppointmentCounts: new Map(),
    appointmentTypeAppointmentCounts: new Map(),
    appointmentTypeManagerAppointmentCounts: new Map(),
    providerAppointmentCounts: new Map(),
    providerManagerAppointmentCounts: new Map(),
}

export class ProviderSort extends ProviderDepartmentOptionSort {
    static readonly DISPLAY_NAME = new ProviderSort(
        'Display Name', 
        'displayNameCompiled',
        ListSortDirection.ASC,
        [toLower]
    )
    static readonly FIRST_NAME = new ProviderSort(
        'First Name', 
        'firstName',
        ListSortDirection.ASC,
        [toLower]
    )
    static readonly LAST_NAME = new ProviderSort(
        'Last Name', 
        'lastName',
        ListSortDirection.ASC,
        [toLower]
    )
    static readonly MIDDLE_INITIAL = new ProviderSort(
        'Middle Initial', 
        'middleInitial',
        ListSortDirection.ASC,
        [toLower]
    )
    static readonly SPECIALTY = new ProviderSort(
        'Specialty', 
        'specialty',
        ListSortDirection.ASC,
        [toLower]
    )
    static readonly PROVIDER_ID = new ProviderSort(
        'Provider Id', 
        'providerId',
        ListSortDirection.ASC,
        [toLower]
    )
    static readonly APPOINTMENT_COUNT = new ProviderSort(
        'Appointment Count',
        'appointmentCountNode.managerAppointmentCount',
        ListSortDirection.DESC,
        [toInteger]
    )
    static readonly DEPARTMENT_COUNT = new ProviderSort(
        'Deptartment Count',
        'appointmentCountNode.departmentManagerAppointmentCounts.size',
        ListSortDirection.DESC,
        [toInteger]
    )
    static readonly APPOINTMENT_TYPE_COUNT = new ProviderSort(
        'Appointment Type Count',
        'appointmentCountNode.appointmentTypeManagerAppointmentCounts.size',
        ListSortDirection.DESC,
        [toInteger]
    )
    static readonly MANAGER_COUNT = new ProviderSort(
        'Manager Count',
        'managerProviderPermissions.aggregates.distinctCount.managerId',
        ListSortDirection.DESC,
        [toInteger]
    )
    static readonly EXAM_ROOM_COUNT = new ProviderSort(
        'Exam Rooms',
        'examRoomCount',
        ListSortDirection.DESC,
        [toInteger]
    )
    static readonly APPOINTMENT_FETCHING_ENABLED = new ProviderSort(
        'Appt Fetching',
        'fetchAppointmentsEnabled'
    )
    static readonly AUTO_TEXTING_ENABLED = new ProviderSort(
        'Auto Texting',
        'autoTextingEnabledAfter'
    )
    static readonly FAVORITED = new ProviderSort(
        'Is Favorited',
        'isFavorited',
        ListSortDirection.DESC
    )
    static readonly APPOINTMENT_COUNT_TODAY = new ProviderSort(
        "Today's Appointment Counts",
        'appointmentCountToday',
        ListSortDirection.DESC
    )
    static readonly TIME_ONBOARDED = new ProviderSort(
        'Time Onboarded',
        'timeOnboarded'
    )

    static readonly EXCLUDE_FROM_APPOINTMENT_TYPE_CALCULATIONS = new ProviderSort(
        'Exclude from Appt Cycle Estimations',
        'excludeFromAppointmentTypeCalculations'
    )

    static readonly NPI = new ProviderSort(
        'NPI',
        'npi'
    )

    static readonly HAS_TABLEAU_USERS = new ProviderSort(
        'HAS_TABLEAU_USERS',
        'hasTableauUsers'
    )

    static readonly HAS_MANAGER_DASHBOARD_USERS = new ProviderSort(
        'HAS_MANAGER_DASHBOARD_USERS',
        'hasManagerDashboardUsers'
    )
    
    static readonly DELETED_AT = new ProviderSort(
        'DELETED_AT',
        'deletedAt'
    )

    // private to disallow creating other instances of this type
    private constructor(
        public readonly key: string,
        public readonly countPath: string,
        public defaultSort: ListSortDirection = ListSortDirection.ASC,
        public flowFuncs?: ((any: any) => any)[]
    ) {
        super(key, countPath, defaultSort, flowFuncs)
    }

    toString() {
        return this.key
    }
}

export type ProviderSortKey = keyof typeof ProviderSort

interface UseSortProvidersProps {
    initialSortKey?: ProviderSortKey
    initialSortDirection?: ListSortDirection
    initialSecondarySortKey?: ProviderSortKey
    initialSecondarySortDirection?: ListSortDirection
    providers: (ProviderDetailFragment | AdminProviderDetailFragment)[]
    favoriteProviderIds: string[]
    filteredProviderIds?: string[]
    initialSelectedProviderIds?: string[]
    selectedProviderIds?: string[]
    isAdminSite?: boolean
    manager: ManagerDetailFragment
    managers?: ManagerOverviewFragment[]
    appointmentCounts?: Map<string, AppointmentCountItem>
}

export interface UseSortProvidersOutput {
    sortByKey: ProviderSortKey
    sortDirection: ListSortDirection
    secondarySortByKey?: ProviderSortKey
    secondarySortByDirection?: ListSortDirection
    reverseSortDirection: () => void
    setSortDirection: (sortDirection: ListSortDirection) => void
    setSortByKey: (sortByKey: ProviderSortKey) => void
    setSecondarySortByKey: (sortByKey: ProviderSortKey) => void
    setSecondarySortByDirection: (sortDirection: ListSortDirection) => void
    sortedProviders: ExtendedProvider[]
    favoriteProviderIds: string[]
    filteredProviderIds?: string[]
    selectedProviderIds: string[]
    // setSelectedProviderIds: (providerIds: string[])=>void
    // addSelectProviderId: (providerId: string)=>void
    // deselectProviderId: (providerId: string)=>void
    selectedProviders: ExtendedProvider[]
    unselectedProviders: ExtendedProvider[]
}

export function useSortProviders({
    initialSortKey = 'DISPLAY_NAME',
    initialSortDirection = ListSortDirection.ASC,
    initialSecondarySortKey = 'DISPLAY_NAME',
    initialSecondarySortDirection = ListSortDirection.ASC,
    providers,
    favoriteProviderIds = [],
    filteredProviderIds,
    selectedProviderIds,
    isAdminSite,
    manager,
    managers,
    appointmentCounts,
}: UseSortProvidersProps): UseSortProvidersOutput {

    providers =
        filteredProviderIds && filteredProviderIds?.length > 0
            ? filter(providers, ({ providerId }) =>
                  includes(filteredProviderIds, providerId)
              )
            : providers
    
    const tableauManagers = findTableauManagersInManagers(managers ?? [])
    const tableauManagerProviderDepartmentPermissionProviderIds = findProviderIdsInManagers(tableauManagers)
    const allManagerProviderDepartmentPermissionProviderIds = findProviderIdsInManagers(managers ?? [])
            
    providers = compact(providers)?.map((p) => {
        const pAny: any = p
        return {
            ...p,
            isFavorited: includes(favoriteProviderIds, p.providerId),
            isSelected: includes(selectedProviderIds, p.providerId),
            appointmentCountNode: appointmentCounts?.get(p.providerId) ?? EMPTY_APPOINTMENT_COUNT_ITEM,
            appointmentCountToday: makeAppointmentStatusCountToday({
                provider: pAny,
                isAdminSite,
                manager
            }),
            ... isArray(managers) ? {
                hasTableauUsers: includes(tableauManagerProviderDepartmentPermissionProviderIds, p.providerId),
                hasManagerDashboardUsers: includes(allManagerProviderDepartmentPermissionProviderIds, p.providerId),
            } : {}
        }
    })

    const { sortedItems: sortedProviders, ...sortController } =
        useSortListItems<ExtendedProvider, ProviderSortKey>({
            items: providers as ExtendedProvider[],
            sortValues: ProviderSort,
            initialSortKey,
            initialSortDirection,
            initialSecondarySortKey,
            initialSecondarySortDirection,
        })

    return {
        ...sortController,
        sortedProviders,
        favoriteProviderIds,
        filteredProviderIds,
        selectedProviderIds: selectedProviderIds ?? [],
        // setSelectedProviderIds,
        // addSelectProviderId: (providerId)=>setSelectedProviderIds(compact(uniq([ providerId, ...selectedProviderIds ])) ),
        // deselectProviderId: (providerId)=>setSelectedProviderIds(compact(uniq(without(selectedProviderIds, providerId)))),
        selectedProviders: filter(sortedProviders, ({ providerId }) =>
            includes(selectedProviderIds, providerId)
        ),
        unselectedProviders: filter(
            sortedProviders,
            ({ providerId }) => !includes(selectedProviderIds, providerId)
        ),
    }
}
